import { useEffect, useState } from "react";
import { IncidentModal } from "../components/IncidentModal";
import { IncidentDetailModal } from "../components/IncidentDetailModal";
import { Icons } from "../components/icons";
import { useGetIncidentsQuery } from "../store/incidents";
import { useServiceSlugMap } from "../store/services";

const STATE_BADGE: Record<string, { class: string; label: string }> = {
  investigating: { class: "major",    label: "Investigando" },
  identified:    { class: "degraded", label: "Identificado" },
  monitoring:    { class: "maint",    label: "En monitoreo" },
  resolved:      { class: "ok",       label: "Resuelto" },
};

export default function Incidents() {
  // RTK Query: refetchOnFocus + invalidación al crear/resolver.
  const { data: incidents = [], refetch } = useGetIncidentsQuery();
  const slugMap = useServiceSlugMap();

  // Cuando el usuario cambia de org en el header, RTK Query no se entera solo
  // (el cache está keyed por endpoint+args, no por header X-Org-Id). Sin esto,
  // la lista muestra los incidentes de la org anterior hasta el próximo
  // refetchOnFocus. Mismo patrón que Services/Subscribers/Traces.
  useEffect(() => {
    window.addEventListener("sovra:org-changed", refetch);
    return () => window.removeEventListener("sovra:org-changed", refetch);
  }, [refetch]);
  const [showModal, setShowModal] = useState(false);
  // Click en una fila → abre el modal de detalle. Antes navegaba a
  // `/incidents/:id`; ahora preferimos mantener al usuario en la lista.
  const [openIncidentId, setOpenIncidentId] = useState<string | null>(null);

  const active = incidents.filter((i) => i.state !== "resolved");

  return (
    <>
      <header className="page-header">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1>Incidentes</h1>
            {active.length > 0 && (
              <span className="badge badge-major text-mono">{active.length} activos</span>
            )}
          </div>
          <div className="sub">
            Eventos operativos que afectan uno o más servicios. Cuando se abren se notifica a los suscriptores;
            los auto-incidents se crean cuando un servicio falla repetidas veces.
          </div>
        </div>
        <button className="btn btn-primary" type="button" onClick={() => setShowModal(true)}>
          <Icons.Plus size={13} sw={2.2} />
          Crear incidente
        </button>
      </header>

      <div className="page-content" style={{ display: "grid", gap: 18 }}>
        <section className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Severidad</th>
                <th>Incidente</th>
                <th style={{ width: 220 }}>Servicios afectados</th>
                <th style={{ width: 140 }}>Estado</th>
                <th style={{ width: 130 }}>Abierto</th>
                <th style={{ width: 90, textAlign: "right" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {incidents.map((inc) => {
                const stateBadge = STATE_BADGE[inc.state] ?? STATE_BADGE.investigating;
                const isAuto = inc.created_by_id === "system";
                return (
                  <tr
                    key={inc.id}
                    onClick={() => setOpenIncidentId(inc.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <span className={`sev-badge sev-${inc.severity}`}>{inc.severity}</span>
                    </td>
                    <td style={{ color: "var(--fg-1)", fontWeight: 500 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span>{inc.title}</span>
                        {isAuto && (
                          <span
                            title="Incidente creado automáticamente por el detector de errores reiterados"
                            style={{
                              fontSize: 10,
                              fontFamily: "var(--font-mono)",
                              padding: "1px 6px",
                              borderRadius: 4,
                              background: "color-mix(in oklab, var(--accent) 12%, transparent)",
                              color: "var(--accent)",
                              fontWeight: 600,
                            }}
                          >
                            auto
                          </span>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 3, color: "var(--fg-4)", fontSize: 11 }}>
                        <span className="mono">{inc.id}</span>
                        {inc.message_public && (
                          <>
                            <span>·</span>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 360 }}>
                              {inc.message_public.slice(0, 80)}
                              {inc.message_public.length > 80 ? "…" : ""}
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      {inc.affected_service_ids.length === 0 ? (
                        <span style={{ color: "var(--fg-4)", fontSize: 12 }}>—</span>
                      ) : (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {inc.affected_service_ids.slice(0, 3).map((s) => (
                            <span key={s} className="svc-chip" title={s}>
                              <span className="dot status-dot major" />
                              {slugMap[s] ?? s.slice(0, 8)}
                            </span>
                          ))}
                          {inc.affected_service_ids.length > 3 && (
                            <span className="svc-chip">+{inc.affected_service_ids.length - 3}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${stateBadge.class}`}>
                        <span className="dot" style={{ background: "currentColor" }} />
                        {stateBadge.label}
                      </span>
                    </td>
                    <td className="mono" style={{ color: "var(--fg-3)", fontSize: 12 }}>
                      {new Date(inc.opened_at).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" })}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        type="button"
                        className="btn btn-icon btn-ghost"
                        title="Ver detalle"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenIncidentId(inc.id);
                        }}
                        style={{ width: 28, height: 28 }}
                      >
                        <Icons.External size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {incidents.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 0 }}>
                    <div className="empty" style={{ padding: 48 }}>
                      <div className="ico"><Icons.Flag size={20} /></div>
                      <div>Sin incidentes registrados.</div>
                      <div className="text-sm" style={{ marginTop: 6 }}>
                        Se crean automáticamente cuando un servicio falla en cadena, o manualmente con el botón "Crear incidente".
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>

      {showModal && <IncidentModal onClose={() => setShowModal(false)} />}
      {openIncidentId && (
        <IncidentDetailModal
          incidentId={openIncidentId}
          onClose={() => setOpenIncidentId(null)}
        />
      )}
    </>
  );
}
